import is from '@sindresorhus/is';
import { motion } from 'framer-motion';
import type { FunctionComponent, ReactNode } from 'react';
import { useRef } from 'react';
import { useNavigate, useParams } from 'react-router';

import { useTranslate } from '~/shared/context/use-translate';
import type { PrimaryGoalType } from '~/shared/data-contract/job-architecture';
import type { TranslationKey } from '~/shared/internationalization/translation';
import { Morph } from '~/ui/animations/morph/Morph';
import { Button } from '~/ui/core/button/Button';
import { IconIndicator } from '~/ui/core/icon-indicator/IconIndicator';
import { EuropeIcon, IconArrowsRepeatCircle, IconCheckmark1Small, IconDocument, IconEdit } from '~/ui/core/icons';
import { Spinner } from '~/ui/core/spinner/Spinner';
import { Textarea } from '~/ui/core/textarea/Textarea';

import { useSnackbars } from '../../app/error-handling/SnackBars';
import { useJobArchitectureMetadata } from '../api/use-job-architecture-metadata';
import { useUpdateJobArchitectureMetadata } from '../api/use-update-job-architecture-metadata';
import { Container, Description, Footer, FooterActions, Title, TitleGroup, WidthLimiter } from './setup-dialog-styles';
import { useGoalSelection } from './use-goal-selection';

type GoalOption = {
    readonly desc: TranslationKey;
    readonly icon: ReactNode;
    readonly label: TranslationKey;
    readonly value: PrimaryGoalType;
};

const GOALS: readonly GoalOption[] = [
    {
        desc: 'pages.job-architecture-v2.setup-dialog.overview.goals.reorganization-desc',
        icon: <IconArrowsRepeatCircle height={20} width={20} />,
        label: 'pages.job-architecture-v2.setup-dialog.overview.goals.reorganization',
        value: 'reorganization',
    },
    {
        desc: 'pages.job-architecture-v2.setup-dialog.overview.goals.eu-pay-transparency-desc',
        icon: <EuropeIcon height={20} width={20} />,
        label: 'pages.job-architecture-v2.setup-dialog.overview.goals.eu-pay-transparency',
        value: 'eu_pay_transparency',
    },
    {
        desc: 'pages.job-architecture-v2.setup-dialog.overview.goals.sap-success-factors-desc',
        icon: <IconDocument height={20} width={20} />,
        label: 'pages.job-architecture-v2.setup-dialog.overview.goals.sap-success-factors',
        value: 'sap_success_factors',
    },
];

const ease = [0.16, 1, 0.3, 1] as const;

const otherCardTransition = { delay: 0.1 + GOALS.length * 0.07, duration: 0.45, ease } as const;

const cardClasses = (isSelected: boolean): string =>
    [
        'flex w-full items-start gap-3xl rounded-xl border bg-white-200 cursor-pointer text-left',
        'pt-4xl px-3xl pb-3xl',
        'transition duration-200 ease',
        isSelected ? 'border-orange-500 shadow-md' : 'border-gray-300 shadow-sm hover:border-gray-400 hover:shadow-md',
    ].join(' ');

type OverviewContentProps = {
    readonly jobArchitectureId: string;
    readonly onContinue: () => void;
};

export const OverviewContent: FunctionComponent<OverviewContentProps> = ({ jobArchitectureId, onContinue }) => {
    const translate = useTranslate();
    const { data: metadata } = useJobArchitectureMetadata(jobArchitectureId);
    const { showErrorSnackbar } = useSnackbars();
    const otherCardRef = useRef<HTMLDivElement>(null);
    const otherTextareaRef = useRef<HTMLTextAreaElement>(null);
    const { isPending, mutateAsync: updateMetadata } = useUpdateJobArchitectureMetadata();
    const { isOtherSelected, otherText, selectedGoals, setOtherText, toggleGoal } = useGoalSelection(
        jobArchitectureId,
        metadata,
    );

    const isOtherIncomplete = isOtherSelected && otherText.trim() === '';

    const handleNextStep = async (): Promise<void> => {
        if (is.emptyArray(selectedGoals)) {
            return;
        }

        try {
            await updateMetadata({
                jobArchitectureId,
                metadata: {
                    primaryGoals: selectedGoals,
                    primaryGoalOtherText: isOtherSelected ? otherText : null,
                },
            });

            onContinue();
        } catch (error) {
            showErrorSnackbar(error);
        }
    };

    const handleOtherCardClick = (): void => {
        const wasSelected = isOtherSelected;
        toggleGoal('other');
        if (!wasSelected) {
            requestAnimationFrame(() => {
                otherTextareaRef.current?.focus({ preventScroll: true });
                otherCardRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
            });
        }
    };

    const handleOtherCardKeyDown = (e: React.KeyboardEvent): void => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleGoal('other');
        }
    };

    const renderGoalCard = (goal: GoalOption, index: number): ReactNode => {
        const isSelected = selectedGoals.includes(goal.value);

        return (
            <motion.button
                key={goal.value}
                type="button"
                animate={{ opacity: 1, y: 0 }}
                aria-pressed={isSelected}
                className={cardClasses(isSelected)}
                initial={{ opacity: 0, y: 12 }}
                transition={{
                    delay: 0.1 + index * 0.07,
                    duration: 0.45,
                    ease,
                }}
                onClick={() => toggleGoal(goal.value)}
            >
                <IconIndicator size="m" variant={isSelected ? 'highlighted' : 'default'}>
                    {goal.icon}
                </IconIndicator>
                <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium tracking-tight text-black-900 mb-xs block">
                        {translate(goal.label)}
                    </span>
                    <span className="text-xs font-normal text-black-500 leading-normal">{translate(goal.desc)}</span>
                </div>
                <span
                    className={[
                        'shrink-0 flex items-center justify-center w-5xl h-5xl rounded-full bg-orange-500 text-white-0',
                        isSelected ? 'visible' : 'invisible',
                    ].join(' ')}
                >
                    <IconCheckmark1Small height={14} width={14} />
                </span>
            </motion.button>
        );
    };

    return (
        <Container>
            <TitleGroup>
                <WidthLimiter>
                    <Title>{translate('pages.job-architecture-v2.setup-dialog.overview.section-title')}</Title>
                    <Description>
                        {translate('pages.job-architecture-v2.setup-dialog.overview.section-description')}
                    </Description>
                </WidthLimiter>
            </TitleGroup>
            <section className="flex flex-1 flex-col justify-center gap-xl">
                {GOALS.map(renderGoalCard)}
                <motion.div
                    ref={otherCardRef}
                    animate={{ opacity: 1, y: 0 }}
                    aria-pressed={isOtherSelected}
                    className={cardClasses(isOtherSelected)}
                    initial={{ opacity: 0, y: 12 }}
                    role="button"
                    tabIndex={0}
                    transition={otherCardTransition}
                    onClick={handleOtherCardClick}
                    onKeyDown={handleOtherCardKeyDown}
                >
                    <IconIndicator size="m" variant={isOtherSelected ? 'highlighted' : 'default'}>
                        <IconEdit height={20} width={20} />
                    </IconIndicator>
                    <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium tracking-tight text-black-900 mb-xs block">
                            {translate('pages.job-architecture-v2.setup-dialog.overview.goals.other')}
                        </span>
                        <span className="text-xs font-normal text-black-500 leading-normal">
                            {translate('pages.job-architecture-v2.setup-dialog.overview.goals.other-desc')}
                        </span>
                        {isOtherSelected && (
                            <div className="mt-xl">
                                <Textarea
                                    ref={otherTextareaRef}
                                    autoResize={true}
                                    placeholder={translate(
                                        'pages.job-architecture-v2.setup-dialog.overview.goals.other-placeholder',
                                    )}
                                    value={otherText}
                                    onChange={(e) => setOtherText(e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </div>
                        )}
                    </div>
                    <span
                        className={[
                            'shrink-0 flex items-center justify-center w-5xl h-5xl rounded-full transition-colors duration-200',
                            isOtherSelected ? 'bg-orange-500 text-white-0' : 'bg-transparent text-transparent',
                        ].join(' ')}
                    >
                        <IconCheckmark1Small height={14} width={14} />
                    </span>
                </motion.div>
            </section>
            <Footer>
                <FooterActions style={{ justifyContent: 'flex-end' }}>
                    <Button
                        disabled={is.emptyArray(selectedGoals) || isPending || isOtherIncomplete}
                        onClick={handleNextStep}
                    >
                        {translate('common.continue')}
                        <Morph direction="vertical" stateKey={isPending ? 'loading' : 'idle'}>
                            {isPending ? <Spinner /> : null}
                        </Morph>
                    </Button>
                </FooterActions>
            </Footer>
        </Container>
    );
};

export const OverviewPage: FunctionComponent = () => {
    const navigate = useNavigate();
    const { id: jobArchitectureId } = useParams<{ id: string }>();

    if (is.undefined(jobArchitectureId)) {
        return null;
    }

    return (
        <OverviewContent jobArchitectureId={jobArchitectureId} onContinue={() => navigate('../company-information')} />
    );
};
